import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronLeft, Instagram, Facebook } from "lucide-react";
import { useLang } from "../../i18n";
import { useLenis } from "../../lib/motion.jsx";
import { LangSwitch } from "../../components/LangSwitch";
import { SHOP_URL } from "../../config";

const ShopLink = ({ t, className }) =>
  SHOP_URL ? (
    <a href={SHOP_URL} target="_blank" rel="noreferrer" data-testid="fc-nav-clubshop" className={className}>{t("nav_clubshop")}</a>
  ) : (
    <NavLink to="/fc-inkad/clubshop" data-testid="fc-nav-clubshop" className={className}>{t("nav_clubshop")}</NavLink>
  );

const LOGO_FC = "/inkad-logo.png";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

export default function FcLayout() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useLenis();
  useEffect(() => { window.scrollTo(0, 0); setOpen(false); }, [pathname]);

  const sport = pathname.startsWith("/fc-inkad/futsal") ? "futsal"
    : pathname.startsWith("/fc-inkad/veldvoetbal") ? "veldvoetbal" : null;
  const other = sport === "futsal" ? "veldvoetbal" : "futsal";
  const sportKey = (s) => (s === "futsal" ? "nav_futsal" : "nav_veldvoetbal");

  const navItems = sport
    ? [
        { to: `/fc-inkad/${sport}`, key: sportKey(sport), end: true },
        { to: `/fc-inkad/${sport}/wedstrijden`, key: "nav_wedstrijden" },
        { to: "/fc-inkad/ploegen", key: "nav_ploegen" },
        { to: `/fc-inkad/${other}`, key: sportKey(other), end: true },
      ]
    : [
        { to: "/fc-inkad/futsal", key: "nav_futsal", end: true },
        { to: "/fc-inkad/veldvoetbal", key: "nav_veldvoetbal", end: true },
        { to: "/fc-inkad/ploegen", key: "nav_ploegen" },
      ];

  const footerItems = [
    { to: "/fc-inkad/futsal", key: "nav_futsal" },
    { to: "/fc-inkad/veldvoetbal", key: "nav_veldvoetbal" },
    { to: "/fc-inkad/ploegen", key: "nav_ploegen" },
  ];

  return (
    <div className="fc-theme fc-noise relative min-h-screen" data-testid="fc-layout">
      {/* Nav */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8 h-16">
          <Link to="/fc-inkad" className="flex items-center gap-3" data-testid="fc-logo-home">
            <img src={LOGO_FC} alt="FC Inkad" className="h-9 w-auto" />
            {sport && <span className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-[#CCFF00] border-l border-white/15 pl-3">{t(sportKey(sport))}</span>}
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={`fc-nav-${it.key}-${it.to.split("/").pop()}`}
                className={({ isActive }) => `px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors duration-150 ${isActive ? "text-[#CCFF00]" : "text-white/70 hover:text-white"}`}>
                {t(it.key)}
              </NavLink>
            ))}
            <ShopLink t={t} className="px-4 py-2 text-sm font-bold uppercase tracking-wide text-white/70 hover:text-white transition-colors duration-150" />
            <Link to="/fc-inkad/inschrijven" data-testid="fc-nav-register"
              className="ml-2 rounded-sm bg-[#CCFF00] px-5 py-2 text-sm font-extrabold uppercase text-black transition-transform duration-150 hover:scale-105">
              {t("nav_inschrijven")}
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/fc-inkad/inschrijven" data-testid="fc-nav-register-mobile"
              className="lg:hidden rounded-sm bg-[#CCFF00] px-3 py-1.5 text-xs font-extrabold uppercase text-black">
              {t("nav_inschrijven")}
            </Link>
            <div className="hidden md:block"><LangSwitch dark /></div>
            <Link to="/" data-testid="fc-back-hub" className="hidden md:flex items-center gap-1 text-xs uppercase tracking-widest text-white/40 hover:text-white transition-colors">
              <ChevronLeft size={14} /> {t("backToInkad")}
            </Link>
            <button className="lg:hidden text-white" onClick={() => setOpen(true)} data-testid="fc-menu-open"><Menu /></button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0A0A0A] lg:hidden" data-testid="fc-mobile-menu">
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
              <img src={LOGO_FC} alt="FC Inkad" className="h-9" />
              <button onClick={() => setOpen(false)} data-testid="fc-menu-close" className="text-white"><X /></button>
            </div>
            <div className="flex flex-col p-6 gap-2">
              {navItems.map((it, i) => (
                <motion.div key={it.to} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.05 }}>
                  <NavLink to={it.to} end={it.end} className="block font-anton text-3xl uppercase text-white py-2">{t(it.key)}</NavLink>
                </motion.div>
              ))}
              <ShopLink t={t} className="block font-anton text-3xl uppercase text-white py-2" />
              <Link to="/fc-inkad/inschrijven" className="mt-4 rounded-sm bg-[#CCFF00] px-6 py-3 text-center font-extrabold uppercase text-black">{t("nav_inschrijven")}</Link>
              <div className="mt-6 flex items-center justify-between">
                <LangSwitch dark />
                <Link to="/" className="text-xs uppercase tracking-widest text-white/40 flex items-center gap-1"><ChevronLeft size={14} />{t("backToInkad")}</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <main><Outlet /></main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0A0A0A] mt-20">
        <div className="mx-auto max-w-7xl px-6 py-14 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <img src={LOGO_FC} alt="FC Inkad" className="h-12 mb-4" />
            <p className="text-sm text-white/50 max-w-sm">{t("fcDesc")}</p>
            <div className="mt-5 flex gap-3">
              <a href="https://www.instagram.com/inkad_diegem/" target="_blank" rel="noreferrer" data-testid="fc-footer-instagram" className="rounded-sm border border-white/15 p-2 text-white/70 hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors" aria-label="Instagram"><Instagram size={18} /></a>
              <a href="https://www.facebook.com/fcinkaddiegem/" target="_blank" rel="noreferrer" data-testid="fc-footer-facebook" className="rounded-sm border border-white/15 p-2 text-white/70 hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors" aria-label="Facebook"><Facebook size={18} /></a>
              <a href="https://www.tiktok.com/@inkad.diegem" target="_blank" rel="noreferrer" data-testid="fc-footer-tiktok" className="rounded-sm border border-white/15 p-2 text-white/70 hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors" aria-label="TikTok"><TikTokIcon size={18} /></a>
            </div>
          </div>
          <div>
            <h4 className="font-anton uppercase text-sm text-white mb-4">FC Inkad</h4>
            <ul className="space-y-2 text-sm text-white/60">
              {footerItems.map((it) => <li key={it.to}><Link to={it.to} className="hover:text-[#CCFF00] transition-colors">{t(it.key)}</Link></li>)}
              <li><ShopLink t={t} className="hover:text-[#CCFF00] transition-colors" /></li>
              <li><Link to="/fc-inkad/inschrijven" className="hover:text-[#CCFF00] transition-colors">{t("nav_inschrijven")}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-anton uppercase text-sm text-white mb-4">{t("contact")}</h4>
            <ul className="space-y-2 text-sm text-white/60">
              <li className="font-semibold text-white/80">Inkad VZW</li>
              <li>Kerktorenstraat 31, 1831 Diegem</li>
              <li>Ondernemingsnr. BE 0743.549.936</li>
              <li><a href="mailto:info@inkad.be" className="hover:text-[#CCFF00] transition-colors">info@inkad.be</a></li>
              <li><a href="tel:+32484331788" className="hover:text-[#CCFF00] transition-colors">0484 33 17 88</a></li>
              <li><Link to="/jeugd" className="text-[#CCFF00] hover:underline">{t("otherBranch")} →</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs text-white/30">© {new Date().getFullYear()} Inkad VZW · Ondernemingsnr. BE 0743.549.936 · {t("footerRights")}</div>
      </footer>
    </div>
  );
}
